/**
 * The kit preview's own filler copy.
 *
 * It is a component gallery, not a screen, so none of this is legacy wording —
 * but it is the surface the kit gets reviewed on, and a gallery half in one
 * language tells you nothing about how the components set in the other.
 */
import { pickLocale } from '../lib/i18n'

const ar = {
  title: 'مكونات الواجهة',
  subtitle: 'معاينة تطويرية للمكونات المشتركة',
  export: 'تصدير',
  add: 'إضافة',
  exportFailed: 'تعذر تنفيذ العملية',

  stats: {
    label: 'بطاقات المؤشرات',
    programs: 'البرامج التدريبية',
    courses: 'الدورات التدريبية',
    passed: 'المجتازون',
    reports: 'بلاغات مفتوحة',
    remaining: 'الحسابات المتبقية',
  },

  table: {
    title: 'قائمة المستخدمين',
    index: 'م',
    userName: 'اسم المستخدم',
    name: 'الاسم الكامل',
    email: 'البريد الإلكتروني',
    groups: 'الإدارات',
    status: 'الحالة',
    editProfile: 'تعديل البيانات الشخصية',
    editStatus: 'تعديل الحالة',
    sendMail: 'إرسال بريد',
    viewDetails: 'مشاهدة التفاصيل',
    search: 'ابحث بالاسم أو البريد',
  },

  states: {
    title: 'الحالات',
    empty: 'لا توجد مدخلات في النظام',
    errorTitle: 'تعذر جلب البيانات',
    errorDesc: 'تحقق من الاتصال بالشبكة ثم أعد المحاولة.',
  },

  form: {
    title: 'عناصر النموذج',
    fullName: 'الاسم الكامل',
    email: 'البريد الإلكتروني',
    emailHint:
      'يستخدم لإرسال التنبيهات ولإعادة تعيين كلمة المرور، وتغييره يتطلب إعادة تفعيل الحساب من جديد.',
    mobile: 'رقم الجوال',
    mobileHelper: 'تسعة أرقام تبدأ بـ 5',
    courseTitle: 'عنوان الدورة',
    retention: 'مدة الاحتفاظ بالسجلات',
    days: 'يوم',
    userName: 'اسم المستخدم',
    userNameError: 'اسم المستخدم مستخدم من قبل',
    expiry: 'تاريخ الانتهاء',
    period: 'فترة الحملة',
    periodFrom: 'من',
    periodTo: 'إلى',
    department: 'الإدارة',
    departmentPick: 'اختر الإدارة',
    departments: [
      'الموارد البشرية', 'تقنية المعلومات', 'أمن المعلومات', 'الشؤون المالية', 'الشؤون القانونية',
      'المشتريات', 'خدمة العملاء', 'التسويق', 'الحوكمة والالتزام', 'التدريب والتطوير',
    ],
    tenant: 'الجهة',
    tenantValue: 'الجهة التجريبية',
    courseSample: 'التوعية بالتصيد الاحتيالي',
    save: 'حفظ',
    delete: 'حذف',
    saved: 'تم حفظ البيانات بنجاح',
  },

  choices: {
    title: 'مربعات الاختيار',
    allDepartments: 'كل الإدارات',
    notify: 'إرسال تنبيه بالبريد',
    remind: 'تذكير قبل الموعد',
    locked: 'غير متاح لهذا الدور',
    selfSignup: 'التسجيل الذاتي',
    selfSignupHint: 'يسمح للموظفين بإنشاء حساباتهم بأنفسهم',
    certificates: 'إصدار الشهادات تلقائيا',
    switches: 'التبديل ورفع الملفات',
    switchesScope: 'تطبق على كل المستخدمين',
    noteIdentity: 'تطبق التغييرات من الدخول التالي للمستخدم.',
    noteWarning: 'إيقاف الحساب يمنع صاحبه من الدخول فورا.',
    noteSuccess: 'تم ربط الدليل النشط بنجاح.',
    reset: 'استعادة الافتراضي',
  },

  badges: {
    active: 'مفعل',
    suspended: 'موقوف',
    waiting: 'بإنتظار التفعيل',
    resigned: 'مستقيل',
    expired: 'منتهي الصلاحية',
    featured: 'مميز',
    blocked: 'محظور',
  },

  buttons: {
    title: 'الأزرار',
    primary: 'إجراء رئيسي',
    secondary: 'ثانوي',
    soft: 'ناعم',
    quiet: 'هادئ',
    danger: 'حذف',
    success: 'اعتماد',
    warning: 'تحذير',
    small: 'صغير',
    medium: 'متوسط',
    large: 'كبير',
    disabled: 'معطل',
    disabledGhost: 'معطل ثانوي',
    edit: 'تعديل',
    view: 'عرض',
  },

  modal: {
    title: 'تعديل البيانات الشخصية',
    hint: 'كل تعديل هنا ينعكس على حساب المستخدم مباشرة بعد الحفظ، ولا يتم إشعاره بذلك.',
    confirm: 'هل تريد حذف هذه الإداره؟',
    deleted: 'تم الحذف بنجاح',
    close: 'إغلاق',
  },
}

const en = {
  title: 'Interface components',
  subtitle: 'Development preview of the shared kit',
  export: 'Export',
  add: 'Add',
  exportFailed: 'The operation could not be completed',

  stats: {
    label: 'Metric cards',
    programs: 'Programs',
    courses: 'Courses',
    passed: 'Passed',
    reports: 'Open reports',
    remaining: 'Remaining Accounts',
  },

  table: {
    title: 'Users List',
    index: '#',
    userName: 'Username',
    name: 'Full name',
    email: 'Email',
    groups: 'Departments',
    status: 'Status',
    editProfile: 'Edit profile',
    editStatus: 'Edit status',
    sendMail: 'Send email',
    viewDetails: 'View details',
    search: 'Search by name or email',
  },

  states: {
    title: 'States',
    empty: 'No matching records found',
    errorTitle: 'The data could not be fetched',
    errorDesc: 'Check the network connection, then try again.',
  },

  form: {
    title: 'Form elements',
    fullName: 'Full name',
    email: 'Email',
    emailHint:
      'Used to send alerts and to reset the password. Changing it requires the account to be activated again.',
    mobile: 'Mobile number',
    mobileHelper: 'Nine digits starting with 5',
    courseTitle: 'Course title',
    retention: 'Record retention',
    days: 'days',
    userName: 'Username',
    userNameError: 'This username is already taken',
    expiry: 'Expiry date',
    period: 'Campaign period',
    periodFrom: 'From',
    periodTo: 'To',
    department: 'Department',
    departmentPick: 'Choose a department',
    departments: [
      'Human resources', 'Information technology', 'Information security', 'Finance', 'Legal affairs',
      'Procurement', 'Customer service', 'Marketing', 'Governance and compliance', 'Training and development',
    ],
    tenant: 'Organization',
    tenantValue: 'Demo organization',
    courseSample: 'Phishing awareness',
    save: 'Save',
    delete: 'Delete',
    saved: 'The data was saved successfully',
  },

  choices: {
    title: 'Checkboxes',
    allDepartments: 'All departments',
    notify: 'Send an email alert',
    remind: 'Remind before the date',
    locked: 'Not available for this role',
    selfSignup: 'Self sign-up',
    selfSignupHint: 'Lets employees create their own accounts',
    certificates: 'Issue certificates automatically',
    switches: 'Switches and uploads',
    switchesScope: 'Applies to every user',
    noteIdentity: 'Changes apply from the next sign-in.',
    noteWarning: 'Suspending an account signs its owner out at once.',
    noteSuccess: 'Active Directory is connected.',
    reset: 'Restore defaults',
  },

  badges: {
    active: 'Enabled',
    suspended: 'Suspended',
    waiting: 'Waiting activation',
    resigned: 'Resigned',
    expired: 'Expired',
    featured: 'Featured',
    blocked: 'Blocked',
  },

  buttons: {
    title: 'Buttons',
    primary: 'Primary action',
    secondary: 'Secondary',
    soft: 'Soft',
    quiet: 'Quiet',
    danger: 'Delete',
    success: 'Approve',
    warning: 'Warning',
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
    disabled: 'Disabled',
    disabledGhost: 'Disabled secondary',
    edit: 'Edit',
    view: 'View',
  },

  modal: {
    title: 'Edit profile',
    hint: 'Every change here reaches the account as soon as it is saved, and the user is not told.',
    confirm: 'Delete this department?',
    deleted: 'Deleted successfully',
    close: 'Close',
  },
}

export const K = pickLocale(ar, en)
